<template>
    <div>
        <div>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-6">
                <!-- Card Number -->
                <div class="md:col-span-4">
                    <div>{{ t('cardNumber') }}</div>
                    <div
                        :id="FIELD_IDS.cardNumber"
                        class="h-[36px] rounded-md border border-gray-300 p-2"
                    />
                </div>

                <!-- Expiration Date -->
                <div class="md:col-span-1">
                    <div>{{ t('expirationDate') }}</div>
                    <div
                        :id="FIELD_IDS.expiry"
                        class="h-[36px] rounded-md border border-gray-300 p-2"
                    />
                </div>

                <!-- CVV -->
                <div class="md:col-span-1">
                    <div>{{ t('securityCode') }}</div>
                    <div
                        :id="FIELD_IDS.cvv"
                        class="h-[36px] rounded-md border border-gray-300 p-2"
                    />
                </div>
            </div>

            <!-- Windcave expects this element even if you don't require it -->
            <div class="mb-8 hidden">
                <div>{{ t('cardholderName') }}</div>
                <div
                    :id="FIELD_IDS.cardholder"
                    class="h-[36px] rounded-md border border-gray-300 p-2"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import {
    PaymentSubmissionResult,
    type TokenPaymentMethod
} from '~/components/Checkout/Sections/Payment/payment-method'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import type { HostedFieldsController } from '~/components/Payments/windcave-types'
import { useShowAlert } from '#imports'
import { useI18n } from 'vue-i18n'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'

const props = defineProps<{
    paymentSourceId: string
    isTokenSession: boolean
    paymentSourceData: {
        url: string
        supportedCards: string[]
        sessionId: string
    }
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
    (e: 'transactionError', payload: string): void
    (e: 'validationError', payload: string): void
}>()

const FIELD_IDS = Object.freeze({
    cardNumber: 'wc-card-number',
    expiry: 'wc-expiry',
    cvv: 'wc-cvv',
    cardholder: 'wc-cardholder-name'
})

const { t } = useI18n({ useScope: 'global' })
const showAlert = useShowAlert()
const rpQuery = useRpQuery()
// These watchdogs are telemetry-only. Windcave still owns the active customer flow until it calls back.
const INITIAL_CALLBACK_WATCHDOG_MILLISECONDS = 75_000
const THREE_DS_DONE_WATCHDOG_MILLISECONDS = 10 * 60 * 1_000

let controller: HostedFieldsController | null = null
const { incrementProcessing, decrementProcessing } = useCheckoutFormProcessing()

async function submitPaymentMethod(
    beforePaymentSubmit?: () => Promise<boolean>
): Promise<PaymentSubmissionResult> {
    let checkoutArmed = false

    if (controller === null) {
        void reportWindcaveCheckoutError(
            'Windcave payment submission was requested without a controller.',
            { stage: 'controller_unavailable' }
        )
        return PaymentSubmissionResult.NOT_SUBMITTED
    }

    if (props.paymentSourceData === undefined) {
        return PaymentSubmissionResult.NOT_SUBMITTED
    }

    const windcaveData = props.paymentSourceData

    incrementProcessing()

    try {
        const valid = await new Promise((resolve, reject) => {
            if (controller === null) {
                reject()
                return
            }

            controller.validateField(
                null,
                result => {
                    try {
                        const allValid = Object.values(result || {}).every(
                            field => field?.isValidationPass === true
                        )
                        resolve(allValid)
                    } catch (error) {
                        reject(error)
                    }
                },
                error => {
                    void reportWindcaveCheckoutError(
                        'Windcave field validation callback failed.',
                        { stage: 'field_validation_error' },
                        error
                    )
                    reject(error)
                }
            )
        })

        if (!valid) {
            emit('validationError', t('payments.checkCardDetails'))
            return PaymentSubmissionResult.NOT_SUBMITTED
        }

        // Arm the checkout only after card fields pass, immediately before payment submission.
        if (
            beforePaymentSubmit !== undefined &&
            !(await beforePaymentSubmit())
        ) {
            return PaymentSubmissionResult.NOT_SUBMITTED
        }
        // The guarded callback returns true only after the backend durably marks checkout ARMED.
        checkoutArmed = beforePaymentSubmit !== undefined

        const paymentSubmissionResult =
            await new Promise<PaymentSubmissionResult>((resolve, reject) => {
                if (controller === null) {
                    reject()
                    return
                }

                const submitStartedAt = Date.now()
                let callbackWatchdog: ReturnType<typeof setTimeout> | undefined

                function clearCallbackWatchdog(): void {
                    if (callbackWatchdog !== undefined) {
                        clearTimeout(callbackWatchdog)
                        callbackWatchdog = undefined
                    }
                }

                function startCallbackWatchdog(
                    stage: string,
                    timeoutMilliseconds: number,
                    status?: string
                ): void {
                    clearCallbackWatchdog()
                    callbackWatchdog = setTimeout(() => {
                        // Intentionally do not settle submission or alter checkout UI here. This timer
                        // only records that Windcave has not honored its documented callback contract.
                        void reportWindcaveCheckoutError(
                            'Windcave did not send the expected final payment callback.',
                            {
                                stage,
                                status,
                                elapsedMilliseconds:
                                    Date.now() - submitStartedAt
                            }
                        )
                    }, timeoutMilliseconds)
                }

                startCallbackWatchdog(
                    'awaiting_initial_submit_callback',
                    INITIAL_CALLBACK_WATCHDOG_MILLISECONDS
                )

                try {
                    controller.submit(
                        windcaveData.url,
                        60,
                        async (status: string) => {
                            if (status === '3DSecure') {
                                // Windcave cancels its initial timeout when 3DS begins,
                                // so our longer watchdog detects a missing final callback.
                                startCallbackWatchdog(
                                    'awaiting_done_after_3ds',
                                    THREE_DS_DONE_WATCHDOG_MILLISECONDS,
                                    status
                                )
                                return
                            }

                            clearCallbackWatchdog()

                            if (status !== 'done') {
                                void reportWindcaveCheckoutError(
                                    'Windcave returned an unexpected submit status.',
                                    {
                                        stage: 'unexpected_submit_status',
                                        status,
                                        elapsedMilliseconds:
                                            Date.now() - submitStartedAt
                                    }
                                )
                                if (!checkoutArmed) {
                                    emit(
                                        'transactionError',
                                        t('payments.transactionFailed', {
                                            code: 10
                                        })
                                    )
                                }
                                return resolve(
                                    checkoutArmed
                                        ? PaymentSubmissionResult.CHECKOUT_STATUS_UNCERTAIN
                                        : PaymentSubmissionResult.NOT_SUBMITTED
                                )
                            }

                            try {
                                if (props.isTokenSession) {
                                    // Tokenize sessions produce a Windcave session first;
                                    // process it now so recurring payment storage receives
                                    // the validated Windcave token expected downstream.
                                    await processWindcaveTokenizePaymentMethod()
                                } else {
                                    emit('paymentMethod', {
                                        type: 'TokenPaymentMethod',
                                        token: 'WC|' + windcaveData.sessionId,
                                        isCard: true,
                                        paymentTodayAllowed: true,
                                        brand: '',
                                        lastFour: null,
                                        accountExpirationDate: null
                                    })
                                }

                                resolve(PaymentSubmissionResult.SUBMITTED)
                            } catch (error) {
                                void reportWindcaveCheckoutError(
                                    'Windcave done callback processing failed.',
                                    {
                                        stage: 'done_callback_processing_error',
                                        status,
                                        elapsedMilliseconds:
                                            Date.now() - submitStartedAt
                                    },
                                    error
                                )
                                reject(error)
                            }
                        },
                        error => {
                            clearCallbackWatchdog()
                            void reportWindcaveCheckoutError(
                                'Windcave submit error callback was invoked.',
                                {
                                    stage: 'submit_error_callback',
                                    elapsedMilliseconds:
                                        Date.now() - submitStartedAt
                                },
                                error
                            )
                            if (!checkoutArmed) {
                                emit(
                                    'transactionError',
                                    t('payments.transactionFailed', {
                                        code: 40
                                    })
                                )
                            }
                            reject(error)
                        }
                    )
                } catch (error) {
                    clearCallbackWatchdog()
                    void reportWindcaveCheckoutError(
                        'Windcave controller submit threw synchronously.',
                        {
                            stage: 'submit_synchronous_error',
                            elapsedMilliseconds: Date.now() - submitStartedAt
                        },
                        error
                    )
                    reject(error)
                }
            })
        return paymentSubmissionResult
    } catch (error) {
        // Report every swallowed provider rejection, then choose UI behavior from the durable boundary.
        void reportWindcaveCheckoutError(
            'Windcave payment submission failed before checkout commit.',
            { stage: 'submit_payment_method_catch' },
            error
        )
        if (checkoutArmed) {
            // The backend must reconcile any provider result once preflight has durably armed checkout.
            return PaymentSubmissionResult.CHECKOUT_STATUS_UNCERTAIN
        }

        emit('transactionError', t('payments.processingProblem', { code: 50 }))
        return PaymentSubmissionResult.NOT_SUBMITTED
    } finally {
        decrementProcessing()
    }
}

async function processWindcaveTokenizePaymentMethod(): Promise<void> {
    try {
        const response = await rpQuery(
            graphql(/* GraphQL */ `
                mutation ProcessWindcavePaymentToken(
                    $input: ProcessCpxPaymentTokenInput!
                ) {
                    processCpxPaymentToken(input: $input) {
                        __typename
                        ... on ProcessCpxPaymentTokenResult {
                            token
                            brand
                            lastFour
                            accountExpirationDate
                        }
                    }
                }
            `),
            {
                input: {
                    paymentSourceId: props.paymentSourceId,
                    sessionId: props.paymentSourceData.sessionId
                }
            }
        )

        if (
            response.processCpxPaymentToken.__typename !==
            'ProcessCpxPaymentTokenResult'
        ) {
            throw new Error()
        }

        emit('paymentMethod', {
            type: 'TokenPaymentMethod',
            token: response.processCpxPaymentToken.token,
            isCard: true,
            paymentTodayAllowed: true,
            brand: response.processCpxPaymentToken.brand,
            lastFour: response.processCpxPaymentToken.lastFour,
            accountExpirationDate:
                response.processCpxPaymentToken.accountExpirationDate
        })
    } catch (error) {
        void reportWindcaveCheckoutError(
            'Windcave token session processing failed.',
            { stage: 'token_session_processing_error' },
            error
        )
        emit(
            'transactionError',
            t('payments.transactionFailed', {
                code: 20
            })
        )
    }
}

async function reportWindcaveCheckoutError(
    message: string,
    context: {
        stage: string
        status?: string
        providerStage?: string
        elapsedMilliseconds?: number
    },
    error?: unknown
): Promise<void> {
    try {
        let errorStack: string | null = null
        if (error instanceof Error) {
            errorStack = error.stack ?? error.message
        } else if (typeof error === 'string') {
            errorStack = error
        } else if (error !== undefined) {
            try {
                // Windcave returns serialized error objects containing fields such as type,
                // reason, and fieldName, so preserve the object instead of logging [object Object].
                errorStack = JSON.stringify(error) ?? String(error)
            } catch {
                // Fall back to the browser's representation for unusual circular provider values.
                errorStack = String(error)
            }
        }

        // Include provider identifiers and browser time so Laravel logs can be
        // matched directly to Windcave session records and reported lost payments.
        await fetch('/error-report', {
            method: 'POST',
            headers: {
                'content-type': 'application/json'
            },
            body: JSON.stringify({
                key: 'checkoutWindcave',
                message,
                stack: errorStack,
                context: {
                    provider: 'Windcave',
                    paymentSourceId: props.paymentSourceId,
                    sessionId: props.paymentSourceData.sessionId,
                    isTokenSession: props.isTokenSession,
                    occurredAt: new Date().toISOString(),
                    ...context
                }
            })
        })
    } catch {
        // Diagnostics must never replace or interfere with checkout behavior.
    }
}

onMounted(async () => {
    // @ts-expect-error: Windcave does not have types
    const HostedFields = window.WindcavePayments?.HostedFields
    if (!HostedFields) {
        void reportWindcaveCheckoutError(
            'Windcave hosted fields were unavailable during initialization.',
            { stage: 'hosted_fields_unavailable' }
        )
        await showAlert(t('headsUp'), t('payments.hostedFieldsUnavailable'))
        return
    }

    controller = HostedFields.create(
        {
            env: 'sec',
            fields: {
                CardNumber: {
                    container: FIELD_IDS.cardNumber,
                    tabOrder: 1,
                    placeholder: t('payments.cardNumberPlaceholder'),
                    supportedCards: [...props.paymentSourceData.supportedCards]
                },
                ExpirationDate: {
                    container: FIELD_IDS.expiry,
                    tabOrder: 2,
                    placeholder: t('payments.expirationDatePlaceholder')
                },
                CVV: {
                    container: FIELD_IDS.cvv,
                    tabOrder: 3,
                    placeholder: t('payments.securityCodePlaceholder')
                }
            },
            styles: {
                'input-invalid': { color: 'red' }
            }
        },
        30,
        () => {
            // Initialization does not own the shared checkout processing lock.
        },
        (providerStage: string, errorDetails: unknown) => {
            // Windcave supplies the generic setup stage first and the actionable serialized error second.
            void reportWindcaveCheckoutError(
                'Windcave hosted fields initialization failed.',
                {
                    stage: 'hosted_fields_initialization_error',
                    providerStage
                },
                errorDetails
            )
            console.error(
                'Hosted Fields init error',
                providerStage,
                errorDetails
            )
            showAlert(t('headsUp'), t('payments.hostedFieldsInitError'))
        }
    )
})

defineExpose({
    submitPaymentMethod
})
</script>
